'use client'

import { Suspense, useCallback, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { EntryDetail } from '@/components/details/entry-detail'
import { ProductShell } from '@/components/layout/product-shell'
import { PageLayout } from '@/components/layout/page-layout'
import { SearchContainer } from '@/components/search/search-container'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { EntryDetailSkeleton } from '@/components/ui/loading-skeletons'
import { useOffline } from '@/components/offline-context'
import { useEntry } from '@/hooks/useEntry'
import { canonicalRoutes } from '@/lib/product-routes'
import {
  getIndexedDictionaryEntryPath,
  hasStaticDictionaryRoute
} from '@/lib/dictionary-route-manifest'
import { analytics } from '@/lib/analytics'
import { cn } from '@/lib/utils'
import {
  IconCloudDownload,
  IconDatabase,
  IconExclamationCircle,
  IconLoader2,
  IconMaximize,
  IconX
} from '@tabler/icons-react'

interface DictPageClientProps {
  title: string
  description: string
}

function DictContent({ title, description }: DictPageClientProps) {
  const searchParams = useSearchParams()
  const q = searchParams.get('q') ?? ''
  const englishMode = searchParams.get('en') === 'true'
  const idParam = searchParams.get('id')
  const id = idParam ? Number(idParam) : undefined
  const fromIdParam = searchParams.get('fromId')
  const fromId = fromIdParam ? Number(fromIdParam) : undefined
  const radParam = searchParams.get('rad')
  const radical = radParam ? Number(radParam) : null
  const {
    isOfflineEnabled,
    isReady,
    progress,
    statusText,
    toggleOffline,
    manifestTiers
  } = useOffline()
  const isSearchOffline = isOfflineEnabled && isReady
  const [isFullScreen, setIsFullScreen] = useState(false)

  const handleToggleOffline = useCallback(() => {
    toggleOffline()
  }, [toggleOffline])

  const updateParams = useCallback(
    (updates: {
      q?: string
      en?: boolean
      id?: number | null
      fromId?: number | null
      rad?: number | null
    }) => {
      const params = new URLSearchParams(searchParams.toString())
      if (updates.q !== undefined) {
        if (updates.q) params.set('q', updates.q)
        else params.delete('q')
      }
      if (updates.en !== undefined) {
        if (updates.en) params.set('en', 'true')
        else params.delete('en')
      }
      if (updates.id !== undefined) {
        if (updates.id !== null) params.set('id', updates.id.toString())
        else params.delete('id')
      }
      if (updates.fromId !== undefined) {
        if (updates.fromId) params.set('fromId', updates.fromId.toString())
        else params.delete('fromId')
      }
      if (updates.rad !== undefined) {
        if (updates.rad !== null) params.set('rad', updates.rad.toString())
        else params.delete('rad')
      }
      return params
    },
    [searchParams]
  )

  const pushDict = useCallback((params: URLSearchParams) => {
    const search = params.toString()
    const href = `${window.location.pathname}${search ? `?${search}` : ''}`

    if (`${window.location.pathname}${window.location.search}` === href) {
      return
    }

    // Query parameters are client-side dictionary state. Native history
    // keeps the workspace mounted while still integrating with
    // useSearchParams and browser back/forward navigation.
    window.history.pushState(null, '', href)
  }, [])

  const onSelectEntry = useCallback(
    (entry: { id: number }, sourceId?: number) => {
      if (hasStaticDictionaryRoute(entry.id)) {
        analytics.dictionaryCleanRouteClickedFromSearch(entry.id)
      }
      const params = updateParams({
        id: entry.id,
        fromId: sourceId,
        q,
        en: englishMode,
        rad: radical
      })
      pushDict(params)
    },
    [pushDict, updateParams, q, englishMode, radical]
  )

  const onSearch = useCallback(
    (newQuery: string) => {
      const params = updateParams({
        q: newQuery,
        en: englishMode,
        id,
        fromId,
        rad: radical
      })
      pushDict(params)
    },
    [pushDict, updateParams, englishMode, id, fromId, radical]
  )

  const onEnglishModeChange = useCallback(
    (newEnglishMode: boolean) => {
      const params = updateParams({
        q,
        en: newEnglishMode,
        id,
        fromId,
        rad: radical
      })
      pushDict(params)
    },
    [pushDict, updateParams, q, id, fromId, radical]
  )

  const clearEntry = useCallback(() => {
    const params = updateParams({ id: null, fromId: null, rad: radical })
    pushDict(params)
  }, [pushDict, updateParams, radical])

  const goBackToFromId = useCallback(
    (targetId: number) => {
      const params = updateParams({
        id: targetId,
        fromId: null,
        q,
        en: englishMode,
        rad: radical
      })
      pushDict(params)
    },
    [pushDict, updateParams, q, englishMode, radical]
  )

  const { data: selectedEntry, isLoading: isEntryLoading } = useEntry(id)
  const showDetail = Boolean(id)

  const workspace = (
    <DictionaryWorkspace
      fullScreen={isFullScreen}
      showDetail={showDetail}
      isSearchOffline={isSearchOffline}
      q={q}
      englishMode={englishMode}
      radical={radical}
      id={id}
      fromId={fromId}
      selectedEntry={selectedEntry ?? undefined}
      isEntryLoading={isEntryLoading}
      onSearch={onSearch}
      onEnglishModeChange={onEnglishModeChange}
      onSelectEntry={onSelectEntry}
      onClearEntry={clearEntry}
      onBackToFromId={goBackToFromId}
      updateParams={updateParams}
      pushDict={pushDict}
    />
  )

  if (isFullScreen) {
    return (
      <ProductShell immersive>
        <div className="bg-background text-foreground relative h-dvh min-w-0 overflow-hidden">
          <div className="absolute top-3 right-3 z-50 flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-background/90 rounded-full shadow-lg backdrop-blur"
              onClick={() => setIsFullScreen(false)}
            >
              <IconX className="h-4 w-4" />
              Exit
            </Button>
          </div>
          {workspace}
        </div>
      </ProductShell>
    )
  }

  return (
    <ProductShell variant="workspace">
      <PageLayout
        variant="workspace"
        title={title}
        description={description}
        headerAction={
          <div className="flex min-w-0 items-center justify-end gap-2">
            <DictionaryOfflineControl
              isOfflineEnabled={isOfflineEnabled}
              isReady={isReady}
              progress={progress}
              statusText={statusText}
              premiumEntryCount={manifestTiers.premium?.entryCount}
              onToggle={handleToggleOffline}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 w-10 shrink-0 rounded-full px-0 xl:w-auto xl:px-3"
              onClick={() => setIsFullScreen(true)}
              aria-label="Open focused dictionary"
            >
              <IconMaximize className="h-4 w-4" />
              <span className="hidden xl:inline">Focus</span>
            </Button>
          </div>
        }
        headerActionCompact={
          <div className="flex items-center gap-2">
            <DictionaryOfflineControl
              compact
              isOfflineEnabled={isOfflineEnabled}
              isReady={isReady}
              progress={progress}
              statusText={statusText}
              premiumEntryCount={manifestTiers.premium?.entryCount}
              onToggle={handleToggleOffline}
            />
            <Button
              type="button"
              variant="outline"
              size="icon-sm"
              className="h-10 w-10 rounded-full"
              aria-label="Open focused dictionary"
              onClick={() => setIsFullScreen(true)}
            >
              <IconMaximize className="h-4 w-4" />
            </Button>
          </div>
        }
        className="text-foreground flex flex-col"
      >
        {workspace}
      </PageLayout>
    </ProductShell>
  )
}

function DictionaryWorkspace({
  fullScreen,
  showDetail,
  isSearchOffline,
  q,
  englishMode,
  radical,
  id,
  fromId,
  selectedEntry,
  isEntryLoading,
  onSearch,
  onEnglishModeChange,
  onSelectEntry,
  onClearEntry,
  onBackToFromId,
  updateParams,
  pushDict
}: {
  fullScreen: boolean
  showDetail: boolean
  isSearchOffline: boolean
  q: string
  englishMode: boolean
  radical: number | null
  id?: number
  fromId?: number
  selectedEntry?: Parameters<typeof EntryDetail>[0]['entry']
  isEntryLoading: boolean
  onSearch: (query: string) => void
  onEnglishModeChange: (englishMode: boolean) => void
  onSelectEntry: (entry: { id: number }, sourceId?: number) => void
  onClearEntry: () => void
  onBackToFromId: (targetId: number) => void
  updateParams: (updates: {
    q?: string
    en?: boolean
    id?: number | null
    fromId?: number | null
    rad?: number | null
  }) => URLSearchParams
  pushDict: (params: URLSearchParams) => void
}) {
  return (
    <div
      className={cn(
        'flex min-h-0 min-w-0 flex-1 flex-col gap-4 md:flex-row',
        fullScreen
          ? 'box-border h-dvh w-full max-w-none overflow-hidden p-3 pt-16 md:gap-5 md:p-4'
          : 'mx-auto w-full max-w-7xl p-4 md:gap-6 md:p-6'
      )}
    >
      <section
        className={cn(
          'min-w-0 flex-col transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
          fullScreen
            ? 'h-full md:flex md:basis-[min(420px,38vw)]'
            : 'md:sticky md:top-20 md:h-[calc(100dvh-7rem)] md:max-h-[calc(100dvh-7rem)] md:basis-4/12',
          showDetail ? 'hidden md:flex' : 'flex'
        )}
      >
        <SearchContainer
          className={cn(
            'h-full min-h-0 flex-1 rounded-2xl',
            fullScreen && 'shadow-lg'
          )}
          isOfflineMode={isSearchOffline}
          initialQuery={q || ''}
          initialEnglishMode={englishMode}
          initialRadical={radical}
          onSearch={onSearch}
          onEnglishModeChange={onEnglishModeChange}
          onRadicalChange={(rad) => {
            const params = updateParams({
              q,
              en: englishMode,
              id,
              fromId,
              rad
            })
            pushDict(params)
          }}
          onSelectEntry={onSelectEntry}
          getResultHref={(result) => {
            const params = updateParams({
              q,
              en: englishMode,
              id: result.id,
              fromId: null,
              rad: radical
            })
            const search = params.toString()
            return `${canonicalRoutes.dict}${search ? `?${search}` : ''}`
          }}
        />
      </section>

      <section
        className={cn(
          'min-w-0 flex-1 transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]',
          fullScreen
            ? 'h-full md:flex'
            : 'md:sticky md:top-20 md:h-[calc(100dvh-7rem)] md:max-h-[calc(100dvh-7rem)]',
          showDetail
            ? 'animate-in fade-in slide-in-from-right-2 flex'
            : 'hidden md:flex'
        )}
      >
        {showDetail ? (
          <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
              <div className="md:hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 rounded-full"
                  onClick={onClearEntry}
                >
                  Back to search
                </Button>
              </div>
              {isEntryLoading ? (
                <EntryDetailSkeleton />
              ) : selectedEntry ? (
                <EntryDetail
                  entry={selectedEntry}
                  className="min-h-full"
                  publicHref={
                    hasStaticDictionaryRoute(selectedEntry.id)
                      ? getIndexedDictionaryEntryPath(selectedEntry)
                      : null
                  }
                  onSelectEntry={(entry) => onSelectEntry(entry, id)}
                  onSearch={onSearch}
                  fromId={fromId}
                  onBack={() => fromId && onBackToFromId(fromId)}
                  onSetRadicalFilter={(rad) => {
                    const params = updateParams({
                      q,
                      en: englishMode,
                      id,
                      fromId,
                      rad
                    })
                    pushDict(params)
                  }}
                />
              ) : (
                <DictionaryEmptyDetail label="Entry not found" />
              )}
            </div>
          </div>
        ) : (
          <DictionaryEmptyDetail label="Select a result to open the entry." />
        )}
      </section>
    </div>
  )
}

function DictionaryEmptyDetail({ label }: { label: string }) {
  return (
    <div className="border-border/70 bg-background/70 text-muted-foreground flex min-h-0 w-full flex-1 items-center justify-center rounded-2xl border border-dashed p-8 text-center text-sm">
      {label}
    </div>
  )
}

function DictionaryOfflineControl({
  compact,
  isOfflineEnabled,
  isReady,
  progress,
  statusText,
  premiumEntryCount,
  onToggle
}: {
  compact?: boolean
  isOfflineEnabled: boolean
  isReady: boolean
  progress: number | null
  statusText?: string | null
  premiumEntryCount?: number
  onToggle: () => void
}) {
  const isDownloading = progress !== null
  const completeCount = premiumEntryCount ?? 122840
  const statusTextValue = statusText ?? ''
  const hasSupportWarning =
    statusTextValue !== '' &&
    statusTextValue !== 'Online' &&
    statusTextValue !== 'Ready' &&
    statusTextValue !== 'Offline mode disabled' &&
    statusTextValue.toLowerCase().includes('not available')
  const status = isDownloading
    ? `${Math.round(progress)}%`
    : isOfflineEnabled && isReady
      ? `Complete ready · ${completeCount.toLocaleString()} entries`
      : hasSupportWarning
        ? 'Unavailable'
        : statusTextValue || 'Online'
  const Icon = isDownloading
    ? IconLoader2
    : hasSupportWarning
      ? IconExclamationCircle
      : isOfflineEnabled
        ? IconDatabase
        : IconCloudDownload

  return (
    <div
      className={cn(
        'border-border/70 bg-background/88 flex min-w-0 items-center gap-3 rounded-full border px-3 py-2 shadow-xs backdrop-blur',
        compact && 'h-10 gap-2 px-2.5 py-1.5'
      )}
    >
      <span
        className={cn(
          'bg-muted text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          isOfflineEnabled && isReady && 'text-primary',
          hasSupportWarning && 'bg-destructive/10 text-destructive'
        )}
        aria-hidden="true"
      >
        <Icon className={cn('h-4 w-4', isDownloading && 'animate-spin')} />
      </span>
      {compact ? (
        <span className="hidden text-xs leading-none font-semibold min-[360px]:block">
          Offline
        </span>
      ) : (
        <span className="min-w-0 flex-1">
          <span className="block text-sm leading-none font-semibold">
            Offline dictionary
          </span>
          <span className="text-muted-foreground mt-1 block truncate text-xs">
            {status}
          </span>
        </span>
      )}
      {hasSupportWarning && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              className="text-destructive hover:bg-destructive/10 focus-visible:ring-ring/50 flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition focus-visible:ring-[3px] focus-visible:outline-none"
              aria-label="Offline dictionary support details"
            >
              <IconExclamationCircle className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            align="end"
            className="bg-popover text-popover-foreground max-w-[min(22rem,calc(100vw-2rem))] px-3 py-2 text-left shadow-lg"
          >
            <span className="block text-xs font-semibold">
              Offline download unavailable
            </span>
            <span className="text-muted-foreground mt-1 block text-xs leading-5">
              Chrome usually supports this on miaozi.co. If it appears here,
              refresh the page, disable strict privacy mode for this site, and
              make sure the address starts with https://miaozi.co.
            </span>
          </TooltipContent>
        </Tooltip>
      )}
      <Switch
        checked={isOfflineEnabled && isReady}
        disabled={isDownloading}
        onCheckedChange={() => onToggle()}
        aria-label="Toggle offline dictionary"
      />
    </div>
  )
}

function DictPageFallback({ title, description }: DictPageClientProps) {
  return (
    <ProductShell variant="workspace">
      <PageLayout variant="workspace" title={title} description={description}>
        <div className="text-muted-foreground flex min-h-[50dvh] items-center justify-center">
          Loading dictionary...
        </div>
      </PageLayout>
    </ProductShell>
  )
}

export function DictPageClient({ title, description }: DictPageClientProps) {
  return (
    <Suspense
      fallback={<DictPageFallback title={title} description={description} />}
    >
      <DictContent title={title} description={description} />
    </Suspense>
  )
}
